import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import ThemeYnSelect from "../forms/ThemeYnSelect";
import { addLogoAndFormHeader, applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";

const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const API_PATH = "/api/sf-009-o-weekly-small-plant-inspection-record";
const ROW_COUNT = 14;

const DEFAULT_ROW = {
  itemType: "",
  description: "",
  itemSerialNumber: "",
  fitForUseYN: "",
  recordDefects: "",
  dateRemovedFromService: "",
  dateReturnedToService: "",
};

const DEFAULT_DATA = {
  competentPersonInspecting: "",
  reportsToPerson: "",
  inspectionRows: Array.from({ length: ROW_COUNT }, () => ({ ...DEFAULT_ROW })),
  signatureOfCompetentPersonInspecting: "",
  dateInspectionCarriedOut: "",
};

const base = () => {
  const url = process.env.REACT_APP_API_URL;
  return url?.endsWith("/") ? url.slice(0, -1) : url || "";
};

const parseDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = parseSiteRegisterDate(v);
  if (d) return d;
  const date = new Date(v);
  return isNaN(date.getTime()) ? null : date;
};

const ymd = (d) => (d && !isNaN(d.getTime()) ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");

const normalizeYN = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return "";
  if (["yes", "y", "true", "1"].includes(s)) return "Y";
  if (["no", "n", "false", "0"].includes(s)) return "N";
  if (s === "y") return "Y";
  if (s === "n") return "N";
  return String(v || "").trim();
};

const getSignatureSrc = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  if (s.startsWith("data:image/")) return s;
  if (/^[A-Za-z0-9+/=\s]+$/.test(s) && s.replace(/\s+/g, "").length > 80) return `data:image/png;base64,${s.replace(/\s+/g, "")}`;
  return "";
};

function mergeData(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const rows = Array.isArray(r.inspectionRows)
    ? r.inspectionRows.map((x) => ({
        itemType: String(x?.itemType || ""),
        description: String(x?.description || ""),
        itemSerialNumber: String(x?.itemSerialNumber || ""),
        fitForUseYN: normalizeYN(x?.fitForUseYN),
        recordDefects: String(x?.recordDefects || ""),
        dateRemovedFromService: String(x?.dateRemovedFromService || ""),
        dateReturnedToService: String(x?.dateReturnedToService || ""),
      }))
    : [];
  while (rows.length < ROW_COUNT) rows.push({ ...DEFAULT_ROW });
  return {
    ...DEFAULT_DATA,
    ...r,
    inspectionRows: rows.slice(0, ROW_COUNT),
  };
}

export default function Sf009OWeeklySmallPlantInspectionRecordPage() {
  const canEditDelete = canEditDeleteRecords();
  const isAdminUser = adminIsPrimaryAdmin();
  const [forms, setForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchForms();
    fetchUsers();
  }, []);

  const rawOf = getRawSf009FormData;

  const fetchForms = async () => {
    setLoadingRecords(true);
    try {
      const res = await fetch(base() + API_PATH, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const json = await res.json();
      setForms(res.ok && json.success && Array.isArray(json.data) ? json.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(base() + "/api/app-users", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const json = await res.json();
      setUsers(res.ok && json.success && Array.isArray(json.data) ? json.data : []);
    } catch {
      setUsers([]);
    }
  };

  const userName = (row) => getSf009AddedByDisplay(row, users);

  const fmt = (v) => {
    const d = parseDate(v);
    return d ? d.toLocaleDateString("en-GB") : "—";
  };

  const filtered = forms.filter((f) => {
    const d = mergeData(rawOf(f));
    const str = `${d.competentPersonInspecting} ${d.reportsToPerson}`.toLowerCase();
    return search
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .every((w) => str.includes(w));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const update = (k, v) => setEditForm((p) => ({ ...p, formData: { ...p.formData, [k]: v } }));
  const updateRow = (i, key, value) =>
    setEditForm((p) => {
      const rows = [...(p.formData.inspectionRows || [])];
      rows[i] = { ...(rows[i] || DEFAULT_ROW), [key]: key === "fitForUseYN" ? normalizeYN(value) : value };
      return { ...p, formData: { ...p.formData, inspectionRows: rows } };
    });

  const handleEdit = (f) => {
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergeData(rawOf(f)),
    });
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editForm?._id) return;
    try {
      const raw = mergeData(editForm.formData);
      const badYn = (raw.inspectionRows || []).some((r) => {
        const v = String(r?.fitForUseYN || "").trim();
        return v !== "" && v !== "Y" && v !== "N";
      });
      if (badYn) {
        toast.error("Fit For Use Y/N accepts only Y or N.");
        return;
      }
      if (raw.signatureOfCompetentPersonInspecting && !getSignatureSrc(raw.signatureOfCompetentPersonInspecting)) {
        toast.error("Signature of competent person inspecting must be a valid signature image.");
        return;
      }
      const body = { ...raw, formUuid: ensureFormUuid(editForm.formUuid) };
      const res = await fetch(base() + API_PATH + "/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        toast.success("Form updated!");
        setShowEditModal(false);
        setEditForm(null);
        fetchForms();
      } else toast.error(json.message || "Failed to update");
    } catch {
      toast.error("Failed to update");
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(base() + API_PATH + "/" + deleteId, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const json = await res.json();
      if (res.ok && json.success) {
        toast.success("Form deleted");
        fetchForms();
      } else toast.error(json.message || "Failed to delete");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const generatePDF = async (row, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;
    const raw = mergeData(rawOf(row));
    const doc = new jsPDF();
    const w = doc.internal.pageSize.getWidth();
    const margin = { left: 10, right: 10 };
    const cw = w - 20;
    const lbl = { fillColor: THEME_LIGHT_RGB, textColor: THEME_RGB, fontStyle: "bold" };
    const val = { fillColor: [255, 255, 255] };
    const grid = { font: "helvetica", fontSize: 7.3, lineWidth: 0.2, cellPadding: 2, valign: "middle" };
    const signatureSrc = getSignatureSrc(raw.signatureOfCompetentPersonInspecting);

    let y = await addLogoAndFormHeader(doc, "Weekly Small Plant Inspection Record");

    autoTable(doc, {
      startY: y,
      theme: "grid",
      tableWidth: cw,
      margin,
      styles: grid,
      body: [
        ["Name of competent person inspecting:", raw.competentPersonInspecting || ""],
        ["Name of person to whom reports were reported:", raw.reportsToPerson || ""],
      ],
      columnStyles: {
        0: { cellWidth: cw * 0.36, ...lbl },
        1: { cellWidth: cw * 0.64, ...val },
      },
    });
    y = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      tableWidth: cw,
      margin,
      styles: { ...grid, minCellHeight: 7.8 },
      head: [[
        { content: "Item Type", styles: lbl },
        { content: "Description", styles: lbl },
        { content: "Item Serial Number", styles: lbl },
        { content: "Fit For Use Y/N", styles: lbl },
        { content: "If N - Record Defects", styles: lbl },
        { content: "Date removed from service", styles: lbl },
        { content: "Date returned to service", styles: lbl },
      ]],
      body: raw.inspectionRows.map((r) => [
        r.itemType || "",
        r.description || "",
        r.itemSerialNumber || "",
        normalizeYN(r.fitForUseYN),
        r.recordDefects || "",
        r.dateRemovedFromService ? fmt(r.dateRemovedFromService) : "",
        r.dateReturnedToService ? fmt(r.dateReturnedToService) : "",
      ]),
      columnStyles: {
        0: { cellWidth: cw * 0.07 },
        1: { cellWidth: cw * 0.16 },
        2: { cellWidth: cw * 0.13 },
        3: { cellWidth: cw * 0.08, halign: "center" },
        4: { cellWidth: cw * 0.23 },
        5: { cellWidth: cw * 0.16 },
        6: { cellWidth: cw * 0.17 },
      },
    });
    y = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      tableWidth: cw,
      margin,
      styles: grid,
      body: [
        ["Signature of competent person inspecting:", " "],
        ["Date Inspection Carried out", raw.dateInspectionCarriedOut ? fmt(raw.dateInspectionCarriedOut) : ""],
      ],
      columnStyles: {
        0: { cellWidth: cw * 0.36, ...lbl },
        1: { cellWidth: cw * 0.64, minCellHeight: 18, ...val },
      },
      didDrawCell: (data) => {
        if (!signatureSrc) return;
        if (data.section === "body" && data.row.index === 0 && data.column.index === 1) {
          try {
            doc.addImage(signatureSrc, "PNG", data.cell.x + 2, data.cell.y + 2, Math.min(40, data.cell.width - 4), Math.min(14, data.cell.height - 4), undefined, "NONE");
          } catch {}
        }
      },
    });

    applyFooterAllPages(doc);
    const localName = `SF009O_${String(raw.competentPersonInspecting || "Record").replace(/[\\/:"*?<>|]+/g, "-")}.pdf`;
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("O", getSf009SharePointFields("O", raw, row, userName))
        : localName;
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${row._id || ""}|${raw.competentPersonInspecting || ""}|${raw.dateInspectionCarriedOut || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-O PDF generated successfully!");
    }
  };

  const fd = editForm?.formData ? mergeData(editForm.formData) : DEFAULT_DATA;

  const sectionHead = {
    borderBottom: `1px solid ${THEME_BORDER}`,
    background: "#fff",
    color: "#1a202c",
    fontWeight: 700,
    fontSize: 15,
    textAlign: "center",
    padding: "14px 12px",
    lineHeight: 1.2,
  };
  const lblCell = {
    border: `1px solid ${THEME_BORDER}`,
    background: THEME_LIGHT,
    color: THEME,
    fontWeight: 700,
    fontSize: 14,
    padding: "4px 6px",
    verticalAlign: "middle",
  };
  const valCell = {
    border: `1px solid ${THEME_BORDER}`,
    background: "#fff",
    padding: "2px 6px",
    verticalAlign: "middle",
  };
  const cellInput = {
    width: "100%",
    border: "none",
    outline: "none",
    fontSize: 13,
    background: "transparent",
  };
  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-O Weekly Small Plant Inspection Record</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Competent Person Inspecting</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Reports To</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Inspection Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((f) => {
                  const d = mergeData(rawOf(f));
                  return (
                    <tr key={f._id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{d.competentPersonInspecting || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{d.reportsToPerson || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{fmt(d.dateInspectionCarriedOut)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{userName(f)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{fmt(f.updatedAt || f.createdAt)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(f)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(f)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${f._id || ""}|${d.competentPersonInspecting || ""}|${d.dateInspectionCarriedOut || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) generatePDF(f, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button
                              onClick={() => {
                                setDeleteId(f._id);
                                setShowDeleteModal(true);
                              }}
                              title="Delete"
                              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                            >
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(26, 32, 44, 0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1180, margin: "0 auto", border: "none", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(139, 22, 52, 0.15)" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>Edit SF-009-O Weekly Small Plant Inspection Record</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "20px 24px 8px", background: "#fff" }}>
                <div style={{ border: `1px solid ${THEME_BORDER}`, background: "#fff" }}>
                  <div style={sectionHead}>Weekly Small Plant Inspection Record</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...lblCell, width: "27%" }}>Name of competent person inspecting:</td>
                        <td style={{ ...valCell, width: "73%" }}>
                          <input value={fd.competentPersonInspecting} onChange={(e) => update("competentPersonInspecting", e.target.value)} style={cellInput} />
                        </td>
                      </tr>
                      <tr>
                        <td style={lblCell}>Name of person to whom reports were reported:</td>
                        <td style={valCell}>
                          <input value={fd.reportsToPerson} onChange={(e) => update("reportsToPerson", e.target.value)} style={cellInput} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ ...lblCell, width: "7%", textAlign: "left" }}>Item Type</th>
                        <th style={{ ...lblCell, width: "15%", textAlign: "left" }}>Description</th>
                        <th style={{ ...lblCell, width: "13%", textAlign: "left" }}>Item Serial Number</th>
                        <th style={{ ...lblCell, width: "8%", textAlign: "left" }}>Fit For Use Y/N</th>
                        <th style={{ ...lblCell, width: "23%", textAlign: "left" }}>If N - Record Defects</th>
                        <th style={{ ...lblCell, width: "17%", textAlign: "left" }}>Date removed from service</th>
                        <th style={{ ...lblCell, width: "17%", textAlign: "left" }}>Date returned to service</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fd.inspectionRows.map((r, i) => (
                        <tr key={i}>
                          <td style={valCell}>
                            <input value={r.itemType || ""} onChange={(e) => updateRow(i, "itemType", e.target.value)} style={cellInput} />
                          </td>
                          <td style={valCell}>
                            <input value={r.description || ""} onChange={(e) => updateRow(i, "description", e.target.value)} style={cellInput} />
                          </td>
                          <td style={valCell}>
                            <input value={r.itemSerialNumber || ""} onChange={(e) => updateRow(i, "itemSerialNumber", e.target.value)} style={cellInput} />
                          </td>
                          <td style={valCell}>
                            <ThemeYnSelect
                              value={normalizeYN(r.fitForUseYN)}
                              onChange={(v) => updateRow(i, "fitForUseYN", v)}
                            />
                          </td>
                          <td style={valCell}>
                            <input value={r.recordDefects || ""} onChange={(e) => updateRow(i, "recordDefects", e.target.value)} style={cellInput} />
                          </td>
                          <td style={valCell}>
                            <DatePicker selected={parseDate(r.dateRemovedFromService)} onChange={(d) => updateRow(i, "dateRemovedFromService", ymd(d))} dateFormat="dd/MM/yyyy" className="form-control form-control-sm border-0 p-0" />
                          </td>
                          <td style={valCell}>
                            <DatePicker selected={parseDate(r.dateReturnedToService)} onChange={(d) => updateRow(i, "dateReturnedToService", ymd(d))} dateFormat="dd/MM/yyyy" className="form-control form-control-sm border-0 p-0" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...lblCell, width: "27%" }}>Signature of competent person inspecting:</td>
                        <td style={{ ...valCell, width: "73%" }}>
                          <div style={{ minHeight: 48, display: "flex", alignItems: "center", justifyContent: "flex-start", background: "#fff" }}>
                            {getSignatureSrc(fd.signatureOfCompetentPersonInspecting) ? (
                              <img src={getSignatureSrc(fd.signatureOfCompetentPersonInspecting)} alt="Signature" style={{ maxHeight: 40, maxWidth: "100%", objectFit: "contain" }} />
                            ) : (
                              <span style={{ color: "#9ca3af", fontSize: 12 }}>No signature available</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td style={lblCell}>Date Inspection Carried out</td>
                        <td style={valCell}>
                          <DatePicker selected={parseDate(fd.dateInspectionCarriedOut)} onChange={(d) => update("dateInspectionCarriedOut", ymd(d))} dateFormat="dd/MM/yyyy" className="form-control form-control-sm border-0 p-0" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer" style={{ background: "#fff", borderTop: `1px solid ${THEME_BORDER}`, padding: "14px 24px", gap: 10 }}>
                <button type="button" className="btn btn-light" style={{ borderRadius: 8, fontWeight: 600, border: `1px solid ${THEME_BORDER}` }} onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 22px", border: "none" }} onClick={saveEdit}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-O Weekly Small Plant Inspection Record</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-O Weekly Small Plant Inspection Record File"
        importPath="sf-009-o-weekly-small-plant-inspection-record/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />
    </>
  );
}
